import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { API_URL } from '../../api/client';
import { UbsCluster } from '../../models/UbsCluster';
import { UbsClustering } from '../../models/UbsClustering';
import { UbsUniprotInfo } from '../../models/UbsUniprotInfo';
import './ubs-uniprot.css';

function clusteringLabel(clustering: UbsClustering): string {
   if (clustering.threshold != null) return clustering.threshold.toFixed(2);
   if (clustering.label.startsWith('LJ') && /^\d+$/.test(clustering.label.slice(2))) {
      return `LJ ${(Number(clustering.label.slice(2)) / 100).toFixed(2)}`;
   }
   return clustering.label;
}

function classGlyph(classification: string | undefined): string {
   if (classification === 'holo') return 'H';
   if (classification === 'apo') return 'A';
   if (classification === 'undefined') return '?';
   return '—';
}

function ClusterRows(props: { cluster: UbsCluster; expanded: boolean; onToggle: () => void }): React.JSX.Element {
   const { cluster } = props;
   const info = cluster.ubs_info;
   const classification = info?.afdb_pocket_classification;
   const known = classification === 'apo' || classification === 'holo' || classification === 'undefined';
   const probability = info?.afdb_mean_probability ?? null;
   const apoVotes = info?.votes_apo_pocket ?? 0;
   const holoVotes = info?.votes_holo_pocket ?? 0;
   const voteTotal = apoVotes + holoVotes;
   const tone = probability == null ? 'weak' : probability >= 0.8 ? 'strong' : probability >= 0.5 ? 'medium' : 'weak';

   return (
      <>
         <tr>
            <td>
               <button type="button" className="ubs-pill" onClick={props.onToggle} title="Show residues and entries">
                  #{cluster.ubs_id}
                  {info?.is_interface ? <span title="Protein–protein interface"> ⧉</span> : null}
                  {info?.n_uniprots != null && info.n_uniprots > 1 ? (
                     <span title={`Shared across ${info.n_uniprots} homologous UniProts`}> ×{info.n_uniprots}</span>
                  ) : null}
               </button>
            </td>
            <td className="num">
               {cluster.num_shared_residues}
               {cluster.num_total_residues !== cluster.num_shared_residues && (
                  <span className="ubs-muted"> / {cluster.num_total_residues}</span>
               )}
            </td>
            <td className="num">{cluster.num_entries}</td>
            <td>
               <span className={`ubs-class ubs-class--${known ? classification : 'unknown'}`}>
                  {known ? classGlyph(classification) : '—'}
                  {probability != null && <span className="ubs-class-prob">{probability.toFixed(2)}</span>}
               </span>
            </td>
            <td className="num">
               {probability == null ? (
                  '—'
               ) : (
                  <span className="ubs-p">
                     {probability.toFixed(2)}
                     <span className="ubs-p-bar">
                        <span
                           className={`ubs-p-fill ubs-p-fill--${tone}`}
                           style={{ width: `${Math.round(probability * 100)}%` }}
                        />
                     </span>
                  </span>
               )}
            </td>
            <td className="num">
               {voteTotal > 0 ? (
                  <span className="ubs-ah" title={`${apoVotes} apo votes, ${holoVotes} holo votes`}>
                     <span className="ubs-ah-bar">
                        <span className="ubs-ah-apo" style={{ width: `${(100 * apoVotes) / voteTotal}%` }} />
                        <span className="ubs-ah-holo" style={{ width: `${(100 * holoVotes) / voteTotal}%` }} />
                     </span>
                     {apoVotes}:{holoVotes}
                  </span>
               ) : (
                  '—'
               )}
            </td>
         </tr>
         {props.expanded && (
            <tr className="ubs-detail-row">
               <td colSpan={6}>
                  <div className="ubs-detail">
                     <div>
                        <strong>Entries</strong>
                        <div className="cluster-list">
                           {cluster.entry_ids.map((entryId) => (
                              <a href={`/db/entry/${entryId}`} target="_blank" rel="noreferrer" key={entryId}>
                                 <span className="cluster-list-item">{entryId}</span>
                              </a>
                           ))}
                        </div>
                     </div>
                     <div>
                        <strong>Core residues</strong>
                        <div className="cluster-list">
                           {cluster.shared_residues.map((residue) => (
                              <span className="cluster-list-item" key={residue}>
                                 {residue}
                              </span>
                           ))}
                        </div>
                     </div>
                     {cluster.num_total_residues !== cluster.num_shared_residues && (
                        <div>
                           <strong>All residues</strong>
                           <div className="cluster-list">
                              {cluster.total_residues.map((residue) => (
                                 <span className="cluster-list-item" key={`all-${residue}`}>
                                    {residue}
                                 </span>
                              ))}
                           </div>
                        </div>
                     )}
                     <div className="ubs-detail-metrics">
                        Density {info?.afdb_normalized_density ?? '—'} · Overlap {info?.afdb_overlap ?? '—'}
                        {info?.representative_job ? (
                           <>
                              {' '}
                              · Representative{' '}
                              <a href={`/db/entry/${info.representative_job}`}>{info.representative_job}</a>
                           </>
                        ) : null}
                     </div>
                  </div>
               </td>
            </tr>
         )}
      </>
   );
}

interface UbsUniprotComponentProps {
   isInsideDbEntryTab: boolean;
   ubs_info: UbsUniprotInfo;
}

const UbsUniprotComponent = (props: UbsUniprotComponentProps): React.JSX.Element => {
   const [activeClusteringTab, setActiveClusteringTab] = useState<number>(0);
   const [plotsOpen, setPlotsOpen] = useState<boolean>(true);
   const [plotTab, setPlotTab] = useState<'dendrogram' | 'positions'>('dendrogram');
   const [expandedCluster, setExpandedCluster] = useState<string | null>(null);
   const dendrogramContainerRef = useRef<HTMLDivElement>(null);
   const positionsContainerRef = useRef<HTMLDivElement>(null);
   const location = useLocation();

   useEffect(() => {
      const loadDendrogram = async (): Promise<void> => {
         if (!props.ubs_info.dendrogram_url || !dendrogramContainerRef.current) return;

         try {
            const response = await fetch(`${API_URL}/${props.ubs_info.dendrogram_url}`);
            if (!response.ok) {
               throw new Error(`HTTP error! status: ${response.status}`);
            }
            const svgText = await response.text();

            if (dendrogramContainerRef.current) {
               dendrogramContainerRef.current.innerHTML = svgText;
               const svgElement = dendrogramContainerRef.current.querySelector('svg');
               if (svgElement) {
                  modifySvg(svgElement);
               }
            }
         } catch (error) {
            console.error('Error loading dendrogram:', error);
            if (dendrogramContainerRef.current) {
               dendrogramContainerRef.current.innerHTML = '<div class="error-message">Unable to load dendrogram</div>';
            }
         }
      };

      void loadDendrogram();
   }, [props.ubs_info.dendrogram_url, props.ubs_info.uniprot_id, plotsOpen, plotTab]);

   useEffect(() => {
      const loadPositions = async (): Promise<void> => {
         if (!props.ubs_info.binding_positions_url || !positionsContainerRef.current) return;
         try {
            const response = await fetch(`${API_URL}/${props.ubs_info.binding_positions_url}`);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const svgText = await response.text();
            if (positionsContainerRef.current) positionsContainerRef.current.innerHTML = svgText;
         } catch (error) {
            console.error('Error loading binding positions:', error);
            if (positionsContainerRef.current) {
               positionsContainerRef.current.innerHTML =
                  '<div class="error-message">Unable to load binding positions</div>';
            }
         }
      };
      void loadPositions();
   }, [props.ubs_info.binding_positions_url, props.ubs_info.uniprot_id, plotsOpen, plotTab]);

   const getCurrentEntryKey = (): string | null => {
      const match = location.pathname.match(/\/db\/entry\/([^/]+)/);
      return match ? match[1] : null;
   };

   const modifySvg = (svg: SVGSVGElement): void => {
      const currentEntryKey = getCurrentEntryKey();
      const groupId = 'matplotlib.axis_2';
      const group = svg.querySelector(`g[id="${groupId}"]`);

      if (!group) return;

      const textElements = group.querySelectorAll('text');

      textElements.forEach((textEl) => {
         const textContent = textEl.textContent?.trim();
         if (!textContent) return;

         const parts = textContent.split(' ');
         const entryKey = parts[0];

         // Create link URL
         const linkUrl = `/db/entry/${encodeURIComponent(entryKey)}`;

         // Get original styles
         const originalFontSize = textEl.getAttribute('font-size') || '8px';
         const originalFontFamily = textEl.getAttribute('font-family') || 'Helvetica Neue';

         // Clear content
         textEl.textContent = '';

         // Create link
         const linkElement = document.createElementNS('http://www.w3.org/2000/svg', 'a');
         linkElement.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', linkUrl);
         linkElement.setAttribute('target', '_blank');

         const titleElement = document.createElementNS('http://www.w3.org/2000/svg', 'title');
         titleElement.textContent = `Open page for AHoJ-DB entry ${entryKey}`;
         linkElement.appendChild(titleElement);

         const isCurrentEntry = currentEntryKey && entryKey === currentEntryKey;

         // Add arrow if current entry
         if (isCurrentEntry) {
            const arrowTspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
            arrowTspan.textContent = '▶ ';
            arrowTspan.style.fill = '#000000';
            arrowTspan.style.fontSize = originalFontSize;
            arrowTspan.style.fontFamily = originalFontFamily;
            linkElement.appendChild(arrowTspan);
         }

         const linkTspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
         linkTspan.textContent = entryKey;
         linkTspan.style.fontSize = originalFontSize;
         linkTspan.style.fontFamily = originalFontFamily;
         linkTspan.style.cursor = 'pointer';

         if (isCurrentEntry) {
            linkTspan.style.fill = '#e53e3e';
            linkTspan.style.fontWeight = 'bold';
            linkTspan.style.textDecoration = 'underline';
         } else {
            linkTspan.style.fill = '#1976d2';
            linkTspan.style.textDecoration = 'underline';
         }

         // Hover effect
         linkTspan.addEventListener('mouseenter', () => {
            linkTspan.style.fill = '#ff9800';
         });
         linkTspan.addEventListener('mouseleave', () => {
            linkTspan.style.fill = isCurrentEntry ? '#e53e3e' : '#1976d2';
         });

         linkElement.appendChild(linkTspan);
         textEl.appendChild(linkElement);
      });
   };

   return (
      <>
         {props.isInsideDbEntryTab && (
            <div className="ubs-link-container">
               <Link
                  to={`/db/ubs/${props.ubs_info.uniprot_id}`}
                  title={`View dedicated UBS page for ${props.ubs_info.uniprot_id}`}
                  target="_blank"
               >
                  UBS page for {props.ubs_info.uniprot_id}
               </Link>
            </div>
         )}
         <div className="ubs-plots">
            <button type="button" className="ubs-plots-toggle" onClick={(): void => setPlotsOpen((open) => !open)}>
               <span>{plotsOpen ? '▼' : '▶'}</span>
               Structure plots
               <span className="ubs-plots-hint">dendrogram · binding positions</span>
            </button>
            {plotsOpen && (
               <div className="ubs-plots-body">
                  <div className="ubs-plot-tabs">
                     <button
                        type="button"
                        className={plotTab === 'dendrogram' ? 'active' : ''}
                        onClick={(): void => setPlotTab('dendrogram')}
                     >
                        Dendrogram
                     </button>
                     <button
                        type="button"
                        className={plotTab === 'positions' ? 'active' : ''}
                        onClick={(): void => setPlotTab('positions')}
                     >
                        Binding positions
                     </button>
                  </div>
                  {plotTab === 'dendrogram' ? (
                     <div className="dendrogram-container">
                        <p className="ubs-plot-caption">
                           Hierarchical clustering of this protein&apos;s binding sites by residue-set similarity. The
                           horizontal axis is the Jaccard similarity of their binding-residue sets. A leaf links to that
                           database entry.
                        </p>
                        {props.ubs_info.dendrogram_url ? (
                           <div
                              ref={dendrogramContainerRef}
                              id={`dendrogram-svg-container-${props.ubs_info.uniprot_id}`}
                              className="dendrogram-svg-container"
                              style={{ maxWidth: '100%', height: 'auto' }}
                           >
                              <p className="loading-message">Loading dendrogram for {props.ubs_info.uniprot_id}...</p>
                           </div>
                        ) : (
                           <div className="no-clusters-message">
                              <div>No dendrogram available for {props.ubs_info.uniprot_id}</div>
                           </div>
                        )}
                     </div>
                  ) : (
                     <div className="dendrogram-container">
                        <p className="ubs-plot-caption">
                           Sequence positions contacted across this protein&apos;s binding sites, aligned to the UniProt
                           sequence.
                        </p>
                        {props.ubs_info.binding_positions_url ? (
                           <div
                              ref={positionsContainerRef}
                              className="dendrogram-svg-container"
                              style={{ maxWidth: '100%', height: 'auto' }}
                           >
                              <p className="loading-message">
                                 Loading binding positions for {props.ubs_info.uniprot_id}...
                              </p>
                           </div>
                        ) : (
                           <div className="no-clusters-message">
                              <div>No binding positions plot available for {props.ubs_info.uniprot_id}</div>
                           </div>
                        )}
                     </div>
                  )}
               </div>
            )}
         </div>

         {props.ubs_info.clusterings && (
            <div className="clustering-section">
               <div className="clustering-tabs">
                  <div className="clustering-tabs-label">Clusterings for thresholds:</div>
                  <div className="clustering-tabs-buttons">
                     {props.ubs_info.clusterings.map((clustering, index: number) => (
                        <button
                           className={`clustering-tab${index === activeClusteringTab ? ' active' : ''}`}
                           onClick={(_): void => setActiveClusteringTab(index)}
                           key={index}
                        >
                           {clusteringLabel(clustering)}
                        </button>
                     ))}
                  </div>
               </div>

               {props.ubs_info.clusterings.map((clustering, index: number) => (
                  <div
                     id={`clustering-tab-${props.ubs_info.uniprot_id}-${clustering.label}`}
                     className={`clustering-tab-content${index === activeClusteringTab ? ' active' : ''}`}
                     key={index}
                  >
                     <p className="ubs-threshold-note">
                        Jaccard similarity cut on the binding-residue sets
                        {clustering.threshold != null ? ` (${clustering.threshold.toFixed(2)})` : ''}. Lower merges more
                        sites; 1.00 merges only identical residue sets.
                     </p>

                     {clustering.clusters && clustering.clusters.length > 0 ? (
                        <table className="clustering-table ubs-table">
                           <thead>
                              <tr>
                                 <th title="Cluster of this protein's binding sites at this Jaccard threshold. ⧉ marks a protein–protein interface.">
                                    UBS
                                 </th>
                                 <th
                                    className="num"
                                    title="Core residues shared by every member. The second number is the union across members."
                                 >
                                    Site
                                 </th>
                                 <th
                                    className="num"
                                    title="Structures in this cluster. Open the row to see each entry."
                                 >
                                    Entries
                                 </th>
                                 <th title="AlphaFold class of the unified site: H holo, A apo, ? undefined. Density and overlap are in the details.">
                                    Class
                                 </th>
                                 <th className="num" title="Confidence of the dominant class, max(P apo, P holo).">
                                    Mean P
                                 </th>
                                 <th className="num" title="AlphaFold votes, apo counted before holo.">
                                    Apo:Holo
                                 </th>
                              </tr>
                           </thead>
                           <tbody>
                              {clustering.clusters.map((cluster) => (
                                 <ClusterRows
                                    key={cluster.ubs_id}
                                    cluster={cluster}
                                    expanded={expandedCluster === cluster.ubs_id}
                                    onToggle={(): void =>
                                       setExpandedCluster((current) =>
                                          current === cluster.ubs_id ? null : cluster.ubs_id,
                                       )
                                    }
                                 />
                              ))}
                           </tbody>
                        </table>
                     ) : (
                        <div className="no-clustering-message">
                           <div>No clusters found for this threshold.</div>
                        </div>
                     )}
                  </div>
               ))}
            </div>
         )}
      </>
   );
};

export default UbsUniprotComponent;
